import { useCallback, useRef, useState } from "react";
import {
  ImprovePromptResult,
  PROMPT_IMPROVE_CLICKED_EVENT,
  PROMPT_IMPROVED_EVENT,
  type ImprovePromptRequest,
} from "@common/utils/improve-prompt";
import { useAnalytics } from "../context/AnalyticsContext";
import { useAuthInfo } from "../context/AuthContext";
import { useWordDocument } from "../context/WordDocumentContext";
import { useDioptraFetch } from "./use-dioptra-fetch";
import { documentDisplayName } from "../utils/documentDisplayName";
import { getEnv } from "../utils/envManager";

export type ImproveState = "idle" | "improving";

const SELECTION_SNIPPET_MAX_CHARS = 300;

interface UseImprovePromptArgs {
  draft: string;
  attachments: string[];
  selectedSkills: string[];
  fillComposer: (text: string) => string | null;
}

export interface UseImprovePromptResult {
  improveState: ImproveState;
  nudge: string | null;
  improveError: string | null;
  syncDraft: (draft: string) => void;
  improveDraft: () => Promise<void>;
}

export function useImprovePrompt({
  draft,
  attachments,
  selectedSkills,
  fillComposer,
}: UseImprovePromptArgs): UseImprovePromptResult {
  const { dioptraFetch } = useDioptraFetch();
  const { analytics, sendError } = useAnalytics();
  const { orgHelper } = useAuthInfo();
  const { documentName, selectedText } = useWordDocument();
  const [improveState, setImproveState] = useState<ImproveState>("idle");
  const [nudge, setNudge] = useState<string | null>(null);
  const [improveError, setImproveError] = useState<string | null>(null);
  const filledTextRef = useRef<string | null>(null);

  const syncDraft = useCallback((currentDraft: string) => {
    if (filledTextRef.current !== null && currentDraft !== filledTextRef.current) {
      filledTextRef.current = null;
      setNudge(null);
      setImproveError(null);
    }
  }, []);

  function organisationName(): string {
    const orgId = orgHelper?.getOrgIds()[0];
    return (orgId ? orgHelper?.getOrg(orgId)?.orgName : undefined) ?? "";
  }

  function requestPayload(): ImprovePromptRequest {
    return {
      surface: "word_addin",
      organisation: organisationName(),
      draft,
      open_document: documentName ? documentDisplayName(documentName) : null,
      selected_text: selectedText ? selectedText.slice(0, SELECTION_SNIPPET_MAX_CHARS) : null,
      attachments,
      selected_playbook: null,
      selected_skills: selectedSkills,
      improved: null,
      answers: null,
    };
  }

  async function improveDraft(): Promise<void> {
    if (improveState !== "idle" || !draft.trim()) {
      return;
    }
    analytics?.track(PROMPT_IMPROVE_CLICKED_EVENT, { surface: "word_addin", draft_length: draft.length });
    setImproveError(null);
    setImproveState("improving");
    try {
      const response = await dioptraFetch(`${getEnv("API_BASE_URL")}/improve-prompt`, {
        method: "POST",
        body: JSON.stringify(requestPayload()),
      });
      const result = ImprovePromptResult.parse(response);
      const filledText = fillComposer(result.improved_prompt);
      if (filledText !== null) {
        filledTextRef.current = filledText;
        setNudge(result.nudge);
        analytics?.track(PROMPT_IMPROVED_EVENT, { surface: "word_addin", trace_id: result.trace_id });
      }
    } catch (error) {
      sendError(error as Error, { component: "ChatInput", action: "improve_prompt" });
      setImproveError("Could not improve the prompt. Your draft is unchanged.");
    }
    setImproveState("idle");
  }

  return {
    improveState,
    nudge,
    improveError,
    syncDraft,
    improveDraft,
  };
}
